/**
 * Study Notes Dialog — view and add notes for a study.
 *
 * Loads notes via getStudyNotes, allows adding new notes via addStudyNote.
 */
import { useState, useEffect, useCallback } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Box, CircularProgress, Typography,
  List, ListItem, ListItemText, Divider, IconButton, Tooltip,
} from '@mui/material';
import { Refresh as RefreshIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { api } from '../../api/rpc';
import type { Study, StudyNote } from '../../api/types';
import { gwtStyles } from '../../config/theme';
import { pushError, pushSuccess } from '../../stores/notificationStore';

interface StudyNotesDialogProps {
  open: boolean;
  study: Study | null;
  onClose: () => void;
}

export const StudyNotesDialog = ({ open, study, onClose }: StudyNotesDialogProps) => {
  const [notes, setNotes] = useState<StudyNote[]>([]);
  const [loading, setLoading] = useState(false);
  const [newNoteText, setNewNoteText] = useState('');
  const [saving, setSaving] = useState(false);

  const loadNotes = useCallback(async () => {
    if (!study) return;
    setLoading(true);
    try {
      const result = await api.getStudyNotes(study.objKey);
      setNotes(result.records);
    } catch (e) {
      pushError('Failed to load notes: ' + (e instanceof Error ? e.message : String(e)));
    } finally {
      setLoading(false);
    }
  }, [study]);

  useEffect(() => {
    if (open && study) {
      loadNotes();
      setNewNoteText('');
    }
  }, [open, study, loadNotes]);

  const handleAddNote = async () => {
    if (!study || !newNoteText.trim()) return;
    setSaving(true);
    try {
      await api.addStudyNote(study.objKey, newNoteText.trim());
      pushSuccess('Note added');
      setNewNoteText('');
      await loadNotes();
    } catch (e) {
      pushError('Failed to add note: ' + (e instanceof Error ? e.message : String(e)));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteNote = async (noteKey: string) => {
    if (!study) return;
    try {
      await api.deleteStudyNote(study.objKey, noteKey);
      pushSuccess('Note deleted');
      await loadNotes();
    } catch (e) {
      pushError('Failed to delete note: ' + (e instanceof Error ? e.message : String(e)));
    }
  };

  const formatDate = (ts: number) => {
    if (!ts) return '';
    return new Date(ts).toLocaleString();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '6px',
          border: `1px solid ${gwtStyles.borderSubtle}`,
          backgroundColor: gwtStyles.popupBg,
          maxHeight: '80vh',
        },
      }}
    >
      <DialogTitle sx={{ fontSize: 14, fontWeight: 600, pb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span>Study Notes{study ? ` — ${study.name}` : ''}</span>
        <Tooltip title="Refresh">
          <IconButton size="small" onClick={loadNotes} disabled={loading}>
            <RefreshIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : notes.length === 0 ? (
          <Typography sx={{ py: 2, textAlign: 'center', fontSize: 12, color: 'text.secondary' }}>
            No notes for this study
          </Typography>
        ) : (
          <List dense sx={{ border: `1px solid ${gwtStyles.borderSubtle}`, borderRadius: '4px', mb: 2 }}>
            {notes.map((note, i) => (
              <Box key={note.objKey || i}>
                {i > 0 && <Divider />}
                <ListItem
                  secondaryAction={
                    <IconButton edge="end" size="small" onClick={() => handleDeleteNote(note.objKey)}>
                      <DeleteIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  }
                >
                  <ListItemText
                    primary={note.text}
                    secondary={`${note.author} — ${formatDate(note.date)}${note.isPrivate ? ' (Private)' : ''}`}
                    primaryTypographyProps={{ fontSize: 13, whiteSpace: 'pre-wrap' }}
                    secondaryTypographyProps={{ fontSize: 11 }}
                  />
                </ListItem>
              </Box>
            ))}
          </List>
        )}

        {/* Add new note */}
        <Typography variant="body2" sx={{ fontSize: 12, fontWeight: 600, mb: 0.5 }}>
          Add Note
        </Typography>
        <TextField
          fullWidth
          multiline
          minRows={2}
          maxRows={5}
          size="small"
          placeholder="Enter note text…"
          value={newNoteText}
          onChange={(e) => setNewNoteText(e.target.value)}
          sx={{ '& .MuiInputBase-root': { fontSize: 13 } }}
        />
      </DialogContent>
      <DialogActions sx={{ px: 2, pb: 1.5 }}>
        <Button size="small" onClick={onClose} sx={{ textTransform: 'none', fontSize: 12 }}>
          Close
        </Button>
        <Button
          size="small"
          variant="contained"
          disabled={!newNoteText.trim() || saving}
          onClick={handleAddNote}
          sx={{ textTransform: 'none', fontSize: 12 }}
        >
          {saving ? 'Saving…' : 'Add Note'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
